import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import * as pdfjsLib from 'pdfjs-dist';
import { DocumentPlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { KnowledgeBaseFile } from '../../../lib/database';

// Configure the worker to use the local file in the public folder
pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;


interface KnowledgeBaseProps {
  kbFiles: KnowledgeBaseFile[];
  onKbFilesChange: (files: KnowledgeBaseFile[]) => void;
}

const KnowledgeBase: React.FC<KnowledgeBaseProps> = ({ kbFiles, onKbFilesChange }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState<KnowledgeBaseFile | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.type === 'application/pdf') {
      setIsLoading(true);
      try {
        const reader = new FileReader();
        reader.onload = async (event) => {
          if (event.target?.result) {
            const typedArray = new Uint8Array(event.target.result as ArrayBuffer);
            const pdf = await pdfjsLib.getDocument(typedArray).promise;
            let textContent = '';
            for (let i = 1; i <= pdf.numPages; i++) {
              const page = await pdf.getPage(i);
              const text = await page.getTextContent();
              textContent += text.items.map((item: any) => item.str).join(' ');
            }
            const newFile: KnowledgeBaseFile = {
              id: `${Date.now()}-${file.name}`,
              fileName: file.name,
              content: textContent,
            };
            const updatedFiles = [...(kbFiles || []), newFile];
            onKbFilesChange(updatedFiles);
            
            // Add a small delay to ensure loader is visible
            setTimeout(() => {
              setIsLoading(false);
              setIsModalOpen(false);
            }, 500);
          }
        };
        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error('Error parsing PDF:', error);
        setIsLoading(false);
      }
    }
  }, [kbFiles, onKbFilesChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
  });

  const handleRemoveFile = (id: string) => {
    const updatedFiles = kbFiles.filter(file => file.id !== id);
    onKbFilesChange(updatedFiles);
  };

  const openEditModal = (file: KnowledgeBaseFile) => {
    setCurrentFile(file);
    setEditedContent(file.content);
    setIsEditModalOpen(true);
  };

  const handleSaveContent = () => {
    if (currentFile) {
      const updatedFiles = kbFiles.map(file =>
        file.id === currentFile.id ? { ...file, content: editedContent } : file
      );
      onKbFilesChange(updatedFiles);
      setIsEditModalOpen(false);
      setCurrentFile(null);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-lg font-semibold text-gray-900">Knowledge Base</h4>
          <p className="text-sm text-gray-600 mt-1">Upload PDFs for the agent to use as context</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-netlify-teal text-white hover:bg-netlify-600 rounded-lg transition-colors font-medium"
        >
          <DocumentPlusIcon className="w-5 h-5 hidden sm:inline-block" />
          <span>Add File</span>
        </button>
      </div>

      <div className="space-y-4">
        {(kbFiles || []).length === 0 ? (
          <div {...getRootProps()} className={`text-center py-8 border-2 border-dashed rounded-lg transition-colors ${
            isLoading 
              ? 'border-gray-300 bg-gray-50 cursor-not-allowed' 
              : isDragActive 
              ? 'border-netlify-teal bg-netlify-teal bg-opacity-10' 
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <input {...getInputProps()} disabled={isLoading} />
            {isLoading ? (
              <div className="flex flex-col items-center justify-center">
                <div className="w-8 h-8 border-4 border-netlify-teal border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-gray-600 font-medium">Processing PDF...</p>
                <p className="text-gray-500 text-sm mt-1">Please wait while we extract the content</p>
              </div>
            ) : (
              <>
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No files uploaded yet</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">Drag and drop a PDF file here, or click the button to upload</p>
                <button 
                  onClick={(e) => { e.stopPropagation(); setIsModalOpen(true); }}
                  disabled={isLoading}
                  className={`inline-flex items-center px-4 py-2 rounded-lg transition-colors font-medium ${
                    isLoading 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'bg-netlify-teal text-white hover:bg-netlify-600'
                  }`}
                >
                  <DocumentPlusIcon className="w-5 h-5 mr-2 hidden sm:inline-block" />
                  Upload a File
                </button>
              </>
            )}
          </div>
        ) : (
          (kbFiles || []).map((file) => (
            <div key={file.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex-shrink-0 items-center justify-center hidden sm:flex">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium text-gray-900 truncate">{file.fileName}</h5>
                  </div>
                </div>
                <div className="flex items-center space-x-2 mt-3 sm:mt-0 sm:justify-end">
                  <button 
                    onClick={() => openEditModal(file)}
                    className="text-sm text-netlify-teal hover:text-netlify-600 font-medium"
                  >
                    View/Edit
                  </button>
                  <button 
                    onClick={() => handleRemoveFile(file.id!)}
                    className="text-gray-400 hover:text-red-600 p-1 rounded-md transition-colors"
                    aria-label="Remove file"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Upload PDF File</h3>
                <button 
                  onClick={() => !isLoading && setIsModalOpen(false)}
                  disabled={isLoading}
                  className={`transition-colors ${isLoading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div
                {...getRootProps()}
                className={`p-12 border-2 border-dashed rounded-lg text-center transition-colors flex items-center justify-center h-48 ${
                  isLoading 
                    ? 'border-gray-300 bg-gray-50 cursor-not-allowed' 
                    : isDragActive 
                    ? 'border-netlify-teal bg-netlify-teal bg-opacity-10 cursor-pointer' 
                    : 'border-gray-300 hover:border-gray-400 cursor-pointer'
                }`}
              >
                <input {...getInputProps()} disabled={isLoading} />
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-8 h-8 border-4 border-netlify-teal border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-600 font-medium">Processing PDF...</p>
                    <p className="text-gray-500 text-sm mt-1">Please wait while we extract the content</p>
                  </div>
                ) : isDragActive ? (
                  <p className="text-netlify-teal font-medium">Drop the file here ...</p>
                ) : (
                  <div>
                    <DocumentPlusIcon className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">Drag 'n' drop a PDF file here, or <span className="font-medium text-netlify-teal">click to select one</span></p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && currentFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Edit Content: {currentFile.fileName}</h3>
                <button 
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 flex-grow">
              <textarea 
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full h-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent resize-none" 
              />
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
              <button 
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium text-sm"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveContent}
                className="px-4 py-2 bg-netlify-teal text-white hover:bg-netlify-600 rounded-lg font-medium text-sm"
              >
                Save Content
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase;